// frontend/src/components/Layout/HeaderNew.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Building2, Stethoscope, ClipboardList, MapPin, BookOpen } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useClinicInfo } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useTheme } from '../../hooks/useTheme';
import { usePatientAuth } from '../../contexts/PatientAuthContext';

const HeaderNew: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [usefulDropdownOpen, setUsefulDropdownOpen] = useState(false);
  const [mobileUsefulOpen, setMobileUsefulOpen] = useState(false);
  const { data: clinicInfo } = useClinicInfo();
  const clinic = clinicInfo?.[0];
  const { totalItems, openCart } = useCart();
  // Точка входа в личный кабинет пациента: /cabinet если авторизован, иначе — /cabinet/login
  const { isAuthenticated: patientAuthenticated } = usePatientAuth();
  const cabinetHref = patientAuthenticated ? '/cabinet' : '/cabinet/login';
  const cabinetLabel = patientAuthenticated
    ? t('cabinet.tooltipAuthenticated')
    : t('cabinet.tooltipAnonymous');
  const langRef = useRef<HTMLDivElement>(null);
  const usefulRef = useRef<HTMLDivElement>(null);
  const usefulTabletRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangDropdownOpen(false);
      }
      if (
        usefulRef.current && !usefulRef.current.contains(e.target as Node) &&
        usefulTabletRef.current && !usefulTabletRef.current.contains(e.target as Node)
      ) {
        setUsefulDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const top = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) {
        window.scrollTo(0, parseInt(top || '0') * -1);
      }
    }
    return () => {
      const top = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) {
        window.scrollTo(0, parseInt(top || '0') * -1);
      }
    };
  }, [mobileMenuOpen]);

  const navigation = [
    { label: t('navigation.home'), href: '/', icon: Home },
    { label: t('navigation.departments'), href: '/departments', icon: Building2 },
    { label: t('navigation.doctors'), href: '/doctors', icon: Stethoscope },
    { label: t('navigation.servicesAndPrice'), href: '/services', icon: ClipboardList },
    { label: t('navigation.contact'), href: '/contact', icon: MapPin },
  ];

  const usefulPatients = [
    { label: t('navigation.useful_preparation'), href: '/useful/preparations' },
    { label: t('navigation.useful_health_tools'), href: '/useful/health-tools' },
    { label: t('navigation.useful_faq'), href: '/useful/faq' },
  ];

  const usefulDoctors = [
    { label: t('navigation.useful_icd'), href: '/useful/icd' },
    { label: t('navigation.useful_medical_news'), href: '/useful/medical-news' },
    { label: t('navigation.useful_cognitive_biases'), href: '/useful/cognitive-biases' },
  ];

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const langLabels: Record<string, string> = { ru: '🇷🇺', en: '🇬🇧', hy: '🇦🇲' };

  return (
    <header className="sticky top-0 z-50 px-4 sm:px-8 py-3">
      <nav
        className="max-w-[1312px] mx-auto flex items-center justify-between px-4 py-3 rounded-3xl backdrop-blur-md"
        style={{ background: theme === 'dark' ? 'rgba(31,31,31,0.92)' : 'rgba(237,238,242,0.85)' }}
      >
        {/* Left: logo + nav links */}
        <div className="flex items-center gap-9">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src="/logo.svg"
              alt={clinic?.name || 'Elit-Med'}
              className="w-12 h-12"
            />
          </Link>

          {/* Desktop nav — полный текст (xl+) */}
          <div className="hidden xl:flex items-center gap-2">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-2 py-3 rounded-xl text-base font-medium whitespace-nowrap transition-colors duration-150 ${
                  isActive(item.href)
                    ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                    : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Useful dropdown — desktop */}
            <div className="relative" ref={usefulRef}>
              <button
                onClick={() => setUsefulDropdownOpen(v => !v)}
                className={`flex items-center gap-1 px-2 py-3 rounded-xl text-base font-medium whitespace-nowrap transition-colors duration-150 ${
                  location.pathname.startsWith('/useful')
                    ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                    : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                }`}
              >
                {t('navigation.useful')}
                <svg
                  className={`w-4 h-4 transition-transform duration-150 ${usefulDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {usefulDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-[#fdfdfd] dark:bg-[#1f1f1f] rounded-2xl shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)] overflow-hidden min-w-[480px]">
                  <div className="grid grid-cols-2 gap-0">
                    <div className="p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff] mb-2 px-2">
                        {t('navigation.forPatients')}
                      </div>
                      {usefulPatients.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setUsefulDropdownOpen(false)}
                          className="block px-2 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    <div className="p-4 border-l border-[rgba(160,195,219,0.4)] dark:border-[rgba(30,50,64,0.75)]">
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff] mb-2 px-2">
                        {t('navigation.forDoctors')}
                      </div>
                      {usefulDoctors.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setUsefulDropdownOpen(false)}
                          className="block px-2 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tablet nav — только иконки с тултипами (lg, до xl) */}
          <div className="hidden lg:flex xl:hidden items-center gap-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <div key={item.href} className="relative group">
                  <Link
                    to={item.href}
                    className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors duration-150 ${
                      active
                        ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                        : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                    }`}
                  >
                    <Icon size={20} />
                  </Link>
                  {/* Тултип */}
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 bg-[#1f1f1f] dark:bg-[#edeef2] text-[#eeeef0] dark:text-[#454545] shadow-md">
                    {item.label}
                  </div>
                </div>
              );
            })}

            {/* Useful dropdown — tablet */}
            <div className="relative group" ref={usefulTabletRef}>
              <button
                onClick={() => setUsefulDropdownOpen(v => !v)}
                className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors duration-150 ${
                  location.pathname.startsWith('/useful')
                    ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                    : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                }`}
              >
                <BookOpen size={20} />
              </button>
              {/* Тултип (скрывается когда дропдаун открыт) */}
              {!usefulDropdownOpen && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 bg-[#1f1f1f] dark:bg-[#edeef2] text-[#eeeef0] dark:text-[#454545] shadow-md">
                  {t('navigation.useful')}
                </div>
              )}
              {usefulDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-[#fdfdfd] dark:bg-[#1f1f1f] rounded-2xl shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)] overflow-hidden min-w-[480px]">
                  <div className="grid grid-cols-2 gap-0">
                    <div className="p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff] mb-2 px-2">
                        {t('navigation.forPatients')}
                      </div>
                      {usefulPatients.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setUsefulDropdownOpen(false)}
                          className="block px-2 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    <div className="p-4 border-l border-[rgba(160,195,219,0.4)] dark:border-[rgba(30,50,64,0.75)]">
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff] mb-2 px-2">
                        {t('navigation.forDoctors')}
                      </div>
                      {usefulDoctors.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setUsefulDropdownOpen(false)}
                          className="block px-2 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: phone + lang + cart */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Phone */}
          {clinic?.phones?.[0] && (
            <a
              href={`tel:${clinic.phones[0]}`}
              className="border border-[rgba(160,195,219,0.75)] flex items-center justify-center h-14 w-14 rounded-xl text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors"
              title={clinic.phones[0]}
            >
              <svg className="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V21a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
              </svg>
            </a>
          )}

          {/* Theme toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="border border-[rgba(160,195,219,0.75)] flex items-center justify-center h-14 w-14 rounded-xl text-[#599bff] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="5" strokeWidth={2} />
                  <path strokeLinecap="round" strokeWidth={2} d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              )}
            </button>

            {/* Language switcher dropdown */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] flex items-center justify-center h-14 w-14 rounded-xl text-[#0c63e9] dark:text-[#599bff] font-semibold text-sm hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors"
              >
                {langLabels[language] ?? language.toUpperCase()}
              </button>
              {langDropdownOpen && (
                <div className="absolute right-1/2 translate-x-1/2 top-full mt-1 bg-[#fdfdfd] dark:bg-[#1f1f1f] border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] rounded-xl overflow-hidden shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)] z-50 min-w-[56px]">
                  {(['ru', 'en', 'hy'] as const).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setLangDropdownOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-base font-medium hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors ${
                        language === lang ? 'text-[#0c63e9] dark:text-[#599bff] bg-[#c2dbff]/30 dark:bg-[#192e4d]/50' : 'text-[#454545] dark:text-[#e4e4e4]'
                      }`}
                    >
                      {langLabels[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Личный кабинет пациента: контурная иконка — гость, залитая — вошедший */}
          <Link
            to={cabinetHref}
            title={cabinetLabel}
            aria-label={cabinetLabel}
            className="flex items-center justify-center w-14 h-14 rounded-xl text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors shrink-0"
          >
            <svg
              className="w-7 h-7"
              viewBox="0 0 24 24"
              fill={patientAuthenticated ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={patientAuthenticated ? 1 : 2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </Link>

          {/* Cart button */}
          <button
            onClick={openCart}
            className="relative bg-[#0c63e9] flex items-center gap-2 h-14 px-4 rounded-xl text-[#eeeef0] font-medium text-base hover:bg-[#0b47d5] transition-colors whitespace-nowrap"
          >
            {/* Bookcheck icon */}
            <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {t('priceList.cart.title')}
            {totalItems > 0 && (
              <span className="absolute -top-3 -right-3 bg-[#c10007] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center leading-4">
                {totalItems}
              </span>
            )}
          </button>
        </div>

        {/* Mobile: cart button always visible */}
        <button
          onClick={() => openCart()}
          className="lg:hidden relative bg-[#0c63e9] flex items-center gap-2 h-10 px-3 rounded-xl text-[#eeeef0] font-medium text-sm hover:bg-[#0b47d5] transition-colors whitespace-nowrap"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          {t('priceList.cart.title')}
          {totalItems > 0 && (
            <span className="absolute -top-2 -right-2 bg-[#c10007] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
              {totalItems}
            </span>
          )}
        </button>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 rounded-xl text-[#0c63e9] hover:bg-[#d9e5f1] transition-colors"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-x-0 top-[76px] bottom-0 z-40 overflow-y-auto px-4 pb-6"
          style={{ background: theme === 'dark' ? 'rgba(18,18,18,0.98)' : 'rgba(237,238,242,0.98)' }}
        >
          <div className="flex flex-col gap-1 pt-2">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                    : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile: Useful accordion */}
            <button
              onClick={() => setMobileUsefulOpen(!mobileUsefulOpen)}
              className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                location.pathname.startsWith('/useful')
                  ? 'bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]'
                  : 'text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
              }`}
            >
              {t('navigation.useful')}
              <svg
                className={`w-4 h-4 transition-transform duration-150 ${mobileUsefulOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileUsefulOpen && (
              <div className="ml-4 flex flex-col gap-1">
                <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff]">
                  {t('navigation.forPatients')}
                </div>
                {usefulPatients.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[#0c63e9] dark:text-[#599bff]">
                  {t('navigation.forDoctors')}
                </div>
                {usefulDoctors.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 rounded-xl text-sm text-[#454545] dark:text-[#e4e4e4] hover:bg-[#edeef2] dark:hover:bg-[#192e4d] transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile: личный кабинет пациента (иконка + подпись — места хватает) */}
            <Link
              to={cabinetHref}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-[#0c63e9] dark:text-[#599bff] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors"
            >
              <svg
                className="w-5 h-5 shrink-0"
                viewBox="0 0 24 24"
                fill={patientAuthenticated ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={patientAuthenticated ? 1 : 2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {cabinetLabel}
            </Link>

            <div className="flex items-center gap-2 px-4 py-3">
              {(['ru', 'en', 'hy'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    language === lang
                      ? 'bg-[#0c63e9] dark:bg-[#004dc3] text-white'
                      : 'bg-[#fdfdfd] dark:bg-[#2d3741] text-[#454545] dark:text-[#e4e4e4] hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d]'
                  }`}
                >
                  {langLabels[lang]}
                </button>
              ))}
            </div>

          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderNew;
